// conversation.service.ts — the transcript, and what we are willing to tell the
// model about it.
//
// ─── WHY A REDUCER AND NOT `useState` PER TURN ─────────────────────────────
// Two asks can be in flight in principle, and a reply can land after a reset. A
// reducer makes "no matching pending turn" an expressible, testable no-op; a pile
// of setters makes it an accidental append. See `answered` below.
//
// ─── WHY TURN IDS ARE DERIVED AND NOT RANDOM ───────────────────────────────
// `id` is `${askId}:<kind>`, not `crypto.randomUUID()`. Three reasons, in order
// of how much they matter:
//   1. The reducer stays PURE, so every transition in this file is testable
//      without stubbing a global.
//   2. React keys stay stable across a pending → answer swap, so the bubble
//      morphs in place instead of unmounting and remounting (which would drop
//      focus out of an open diagnostics disclosure).
//   3. A turn's id says which ask it belongs to when reading a log.
// `askId` itself is generated by the caller, once, at submit time.
//
// ─── WHY `buildHistory` IS DELIBERATELY LOSSY ──────────────────────────────
// It sends only COMPLETED exchanges. An unanswered question is dropped — see the
// function's own comment; teaching the model a gap is worse than teaching it
// less.

import { HISTORY_CONTENT_MAX_LENGTH, HISTORY_MAX_TURNS } from '../constants';
import type { ChatReply, ChatTurn } from '../schemas/chat.schema';
import type { AiConversation, AiConversationAction, AiTurn } from '../types';

/**
 * How close to the bottom of the transcript still counts as "at the bottom", in
 * CSS pixels.
 *
 * Not zero, because a partially-rendered last line, sub-pixel rounding on a
 * scaled display, and a scroll animation mid-flight all leave a few pixels of
 * slack that would otherwise read as "the operator has scrolled away". Roughly
 * one bubble's padding plus a line of text.
 *
 * This belongs in `constants.ts` with the other tuned numbers; it is local only
 * because `constants.ts` is owned by another lane of this epic and must not be
 * edited from here. Move it on the follow-up.
 */
const AUTO_SCROLL_SLACK_PX = 64;

/** A fresh, empty transcript stamped with the tenant it will be asked as. */
export function createConversation(tenantId: string, conversationId: string): AiConversation {
  return { id: conversationId, tenantId, turns: [] };
}

/**
 * Replace the `pending` turn belonging to `askId`, or return the state untouched.
 *
 * The no-op arm is the load-bearing one. A reply can arrive after the operator
 * hit reset (or switched tenant, which discards the conversation): appending it
 * would resurrect a turn from a transcript that no longer exists, attaching an
 * answer to a question the operator can no longer see. Returning the SAME object
 * reference also lets React bail out of the re-render entirely.
 */
function replacePending(state: AiConversation, askId: string, replacement: AiTurn): AiConversation {
  const index = state.turns.findIndex((turn) => turn.kind === 'pending' && turn.askId === askId);
  if (index === -1) return state;

  // `map` rather than indexed assignment: `noUncheckedIndexedAccess` makes
  // `turns[index]` a `T | undefined`, and this avoids the question entirely.
  const turns = state.turns.map((turn, i) => (i === index ? replacement : turn));
  return { ...state, turns };
}

/** The only legal transitions. Illegal ones are not expressible in `AiConversationAction`. */
export function conversationReducer(
  state: AiConversation,
  action: AiConversationAction,
): AiConversation {
  switch (action.type) {
    case 'ask':
      // The user turn and its pending placeholder are appended TOGETHER, sharing
      // `askId`. Adding them in two dispatches would give the transcript a frame
      // in which a question exists with nothing beneath it, which reads as a
      // dropped request.
      return {
        ...state,
        turns: [
          ...state.turns,
          {
            kind: 'user',
            id: `${action.askId}:user`,
            askId: action.askId,
            text: action.text,
            askedAt: action.now,
          },
          {
            kind: 'pending',
            id: `${action.askId}:pending`,
            askId: action.askId,
            startedAt: action.now,
          },
        ],
      };

    case 'answered':
      return replacePending(state, action.askId, {
        kind: 'answer',
        id: `${action.askId}:answer`,
        askId: action.askId,
        reply: action.reply,
        receivedAt: action.now,
      });

    case 'failed':
      return replacePending(state, action.askId, {
        kind: 'failed',
        id: `${action.askId}:failed`,
        askId: action.askId,
        failure: action.failure,
        // Carried, not recovered by scanning backwards for the question — a
        // backward scan is exactly where an off-by-one resends the wrong text.
        retryText: action.retryText,
      });

    case 'reset':
      return createConversation(action.tenantId, action.conversationId);
  }
}

/** Bound one history entry's content. */
function truncate(content: string): string {
  return content.slice(0, HISTORY_CONTENT_MAX_LENGTH);
}

/**
 * The `history` array to send with the next question.
 *
 * ─── WHAT IS EXCLUDED, AND WHY ─────────────────────────────────────────────
 * Only COMPLETED exchanges are sent. A question still pending, or one that
 * failed, is dropped along with nothing to pair it with. Sending a lone user
 * turn would tell the model "you were asked this and said nothing", and a
 * text-to-SQL model handed a gap tends to fill it — inventing what it must have
 * answered, then answering the follow-up against that invention. The transcript
 * on screen keeps the failed turn; the model does not get it.
 *
 * Assistant content is `reply.answer` ONLY. Never `sql`: the generated query is
 * gated to admin/manager on screen, and putting it in a later request body would
 * route it straight past that gate.
 *
 * ─── WHY SLICING THE LAST N IS SAFE ────────────────────────────────────────
 * Entries are emitted strictly in user/assistant pairs, so the array length is
 * always even, and `HISTORY_MAX_TURNS` is even too — the slice therefore always
 * lands on a user boundary and cannot hand the server a history that opens with
 * an assistant turn. If either of those facts changes, this needs pair-aware
 * trimming.
 */
export function buildHistory(turns: readonly AiTurn[]): ChatTurn[] {
  const answersByAskId = new Map<string, ChatReply>();
  for (const turn of turns) {
    if (turn.kind === 'answer') answersByAskId.set(turn.askId, turn.reply);
  }

  const entries: ChatTurn[] = [];
  for (const turn of turns) {
    if (turn.kind !== 'user') continue;
    const reply = answersByAskId.get(turn.askId);
    if (reply === undefined) continue;
    entries.push({ role: 'user', content: truncate(turn.text) });
    entries.push({ role: 'assistant', content: truncate(reply.answer) });
  }

  // The server documents that it reads only the last 10, so the LAST N — the
  // recent context — is what has any effect. Sending more is pure payload on a
  // field the server does not bound.
  return entries.slice(-HISTORY_MAX_TURNS);
}

/**
 * Whether a stored conversation may still be shown to the current session.
 *
 * ─── WHY THIS EXISTS AT ALL ────────────────────────────────────────────────
 * A wrong `tenant_id` does not produce an error. It produces a 200 with a
 * plausible answer — "There are no accounts." — and NOTHING in the response
 * distinguishes that from a true empty result. So the tenant a transcript was
 * asked as is stamped at creation and checked here on every render; on mismatch
 * the transcript is discarded rather than continued.
 *
 * Empty strings fail the check on BOTH sides deliberately. `'' === ''` would
 * otherwise "match" a conversation that was never bound to a real tenant against
 * a session that has no tenant claim — the exact pair of unknowns this guard is
 * meant to catch.
 */
export function conversationTenantMatches(
  c: AiConversation,
  sessionTenantId: string | null,
): boolean {
  if (sessionTenantId === null || sessionTenantId === '') return false;
  if (c.tenantId === '') return false;
  return c.tenantId === sessionTenantId;
}

/**
 * Whether new content should scroll the transcript into view.
 *
 * False when the operator has scrolled up to re-read an earlier answer: yanking
 * them to the bottom mid-sentence loses their place, and this panel's whole
 * purpose is comparing an answer against something else on screen.
 *
 * A container shorter than its content yields a non-positive distance and
 * therefore `true`, which is correct — there is nothing to scroll away from.
 */
export function shouldAutoScroll(m: {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}): boolean {
  const distanceFromBottom = m.scrollHeight - m.scrollTop - m.clientHeight;
  return distanceFromBottom <= AUTO_SCROLL_SLACK_PX;
}
